/**
 * WhatsAppPage.js - Centro de Notificações WhatsApp
 * Interface completa para gestão de mensagens WhatsApp via Twilio
 * 
 * Features:
 * - Envio de mensagens template
 * - Envio de mensagens personalizadas
 * - Histórico de mensagens
 * - Configuração de Sandbox
 * - QR Code para registo
 */

import { useEffect, useMemo, useState } from 'react';
import { whatsappAPI, servicesAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Clock, CheckCircle, XCircle, 
  Phone, User, FileText, Wrench, AlertTriangle, RefreshCw,
  QrCode, Plus, History, Settings, Sparkles, Zap,
  MessageCircle, CheckCheck, Timer, Ban, ArrowRight
} from 'lucide-react';
import { formatDateTime, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';

// ============================================================================
// CONSTANTS
// ============================================================================

const TEMPLATE_DEFINITIONS = [
  { 
    id: 'warranty_accepted', 
    name: 'Garantia Aceite', 
    icon: CheckCircle, 
    color: 'green',
    description: 'Notificar cliente que a garantia foi aprovada'
  },
  { 
    id: 'warranty_rejected', 
    name: 'Garantia Rejeitada', 
    icon: XCircle, 
    color: 'red',
    description: 'Informar que a garantia expirou com orçamento'
  },
  { 
    id: 'warranty_void', 
    name: 'Garantia Anulada', 
    icon: Ban, 
    color: 'orange',
    description: 'Notificar dano não coberto pela garantia'
  },
  { 
    id: 'sale_completed', 
    name: 'Venda Concluída', 
    icon: FileText, 
    color: 'blue',
    description: 'Confirmação de compra com detalhes'
  },
  { 
    id: 'repair_received', 
    name: 'Equipamento Recebido', 
    icon: Wrench, 
    color: 'purple',
    description: 'Confirmar receção para reparação'
  },
  { 
    id: 'repair_in_progress', 
    name: 'Reparação em Curso', 
    icon: Timer, 
    color: 'cyan',
    description: 'Atualização de progresso da reparação'
  },
  { 
    id: 'repair_completed', 
    name: 'Reparação Concluída', 
    icon: CheckCheck, 
    color: 'emerald',
    description: 'Notificar conclusão e pronto para levantamento'
  }
];

const STATUS_CONFIG = {
  sent: { label: 'Enviado', color: 'blue', icon: Send },
  delivered: { label: 'Entregue', color: 'green', icon: CheckCheck },
  read: { label: 'Lido', color: 'emerald', icon: CheckCheck },
  failed: { label: 'Falhou', color: 'red', icon: XCircle },
  queued: { label: 'Em Fila', color: 'amber', icon: Clock }
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Template Card
const TemplateCard = ({ template, onClick, selected }) => {
  const Icon = template.icon;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full p-4 rounded-xl border-2 text-left transition-all duration-200",
        "hover:shadow-md hover:scale-[1.01] active:scale-[0.99]",
        selected 
          ? `border-${template.color}-400 bg-${template.color}-50` 
          : "border-slate-200 bg-white hover:border-slate-300"
      )}
      data-testid={`template-${template.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "p-2 rounded-lg",
          `bg-${template.color}-100`
        )}>
          <Icon className={cn("h-5 w-5", `text-${template.color}-600`)} />
        </div>
        <div className="flex-1">
          <p className="font-medium text-slate-900">{template.name}</p>
          <p className="text-sm text-slate-500 mt-0.5">{template.description}</p>
        </div>
      </div>
    </button>
  );
};

// Message History Item
const MessageItem = ({ message, statusLabels, templatesById }) => {
  const status = STATUS_CONFIG[message.status] || STATUS_CONFIG.sent;
  const StatusIcon = status.icon;
  
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
      <div className="p-2 rounded-full bg-green-100">
        <MessageCircle className="h-5 w-5 text-green-600" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{message.recipient_phone}</span>
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs",
              `bg-${status.color}-50 text-${status.color}-700 border-${status.color}-200`
            )}
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusLabels[message.status] || status.label}
          </Badge>
        </div>
        
        {message.template_type && (
          <p className="text-sm text-slate-600 mt-1">
            Template: {templatesById[message.template_type]?.name || message.template_type}
          </p>
        )}
        
        <p className="text-sm text-slate-500 mt-2 line-clamp-2">{message.message_body}</p>
        
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDateTime(message.sent_at)}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {message.sent_by_name}
          </span>
        </div>
      </div>
    </div>
  );
};

// Status Indicator
const StatusIndicator = ({ connected, connectedLabel, disconnectedLabel }) => (
  <div className="flex items-center gap-2">
    <span className={cn(
      "relative flex h-3 w-3",
    )}>
      {connected && (
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
      )}
      <span className={cn(
        "relative inline-flex rounded-full h-3 w-3",
        connected ? "bg-green-500" : "bg-red-500"
      )}></span>
    </span>
    <span className={cn(
      "text-sm font-medium",
      connected ? "text-green-600" : "text-red-600"
    )}>
      {connected ? connectedLabel : disconnectedLabel}
    </span>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WhatsAppPage() {
  const { user, hasRole } = useAuth();
  const { t } = useLanguage();
  const canManage = hasRole(['admin', 'store_manager']);
  
  // State
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('send');
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientPhone, setRecipientPhone] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [variables, setVariables] = useState({});
  
  // Dialog state
  const [showSandboxDialog, setShowSandboxDialog] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const templates = useMemo(() => TEMPLATE_DEFINITIONS.map((template) => ({
    ...template,
    name: t(`whatsappPage.templates.${template.id}.name`),
    description: t(`whatsappPage.templates.${template.id}.description`),
  })), [t]);
  const templatesById = useMemo(() => Object.fromEntries(templates.map((template) => [template.id, template])), [templates]);
  const statusLabels = useMemo(() => ({
    sent: t('whatsappPage.status.sent'),
    delivered: t('whatsappPage.status.delivered'),
    read: t('whatsappPage.status.read'),
    failed: t('whatsappPage.status.failed'),
    queued: t('whatsappPage.status.queued'),
  }), [t]);
  
  // ============================================================================
  // DATA LOADING
  // ============================================================================
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      resetFetchTelemetry();
      const [statusRes, historyRes] = await Promise.all([
        safeRequest(() => whatsappAPI.getStatus(), { data: { connected: false } }, { telemetry: { module: 'whatsapp', endpoint: 'whatsapp.status', onEvent: captureFetchTelemetry } }),
        safeRequest(() => whatsappAPI.getHistory({ limit: 50 }), { data: [] }, { telemetry: { module: 'whatsapp', endpoint: 'whatsapp.history', onEvent: captureFetchTelemetry } })
      ]);
      
      setStatus(statusRes.data || { connected: false });
      // Ensure history is always an array
      const historyData = historyRes.data;
      setHistory(Array.isArray(historyData) ? historyData : []);
      
    } catch (error) {
      console.error('Error loading WhatsApp data:', error);
      setHistory([]);
      toast.error('Erro ao carregar dados do WhatsApp');
    } finally {
      setLoading(false);
    }
  };
  const hasWhatsAppData = Boolean(status || history.length || templates.length);
  
  // ============================================================================
  // ACTIONS
  // ============================================================================
  
  const handleSendTemplate = async () => {
    if (!selectedTemplate) {
      toast.error('Selecione um template');
      return;
    }
    if (!recipientPhone) {
      toast.error('Digite o número de telefone');
      return;
    }
    
    setSending(true);
    try {
      await whatsappAPI.sendTemplate({
        recipient_phone: recipientPhone,
        template_type: selectedTemplate,
        variables
      });
      
      toast.success('Mensagem enviada com sucesso!');
      setSelectedTemplate(null);
      setRecipientPhone('');
      setVariables({});
      loadData();
      
    } catch (error) {
      console.error('Error sending template:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };
  
  const handleSendCustom = async () => {
    if (!recipientPhone) {
      toast.error('Digite o número de telefone');
      return;
    }
    if (!customMessage) {
      toast.error('Digite a mensagem');
      return;
    }
    
    setSending(true);
    try {
      await whatsappAPI.sendCustom({
        recipient_phone: recipientPhone,
        message: customMessage
      });
      
      toast.success('Mensagem personalizada enviada!');
      setCustomMessage('');
      loadData();
      
    } catch (error) {
      console.error('Error sending custom message:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };
  
  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error('Digite o número para teste');
      return;
    }
    
    setSendingTest(true);
    try {
      await whatsappAPI.sendTest(testPhone);
      toast.success('Mensagem de teste enviada!');
      setShowSandboxDialog(false);
      setTestPhone('');
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setSendingTest(false);
    }
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30 p-6" data-testid="whatsapp-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-200">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              WhatsApp Business
            </h1>
            <p className="text-sm text-slate-500">
              {t('whatsappPage.subtitle')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <StatusIndicator
            connected={status?.connected}
            connectedLabel={t('whatsappPage.connected')}
            disconnectedLabel={t('whatsappPage.disconnected')}
          />
          
          <Button 
            variant="outline" 
            onClick={() => setShowSandboxDialog(true)}
            className="gap-2"
          >
            <QrCode className="h-4 w-4" />
            {t('whatsappPage.sandbox')}
          </Button>
          
          <Button 
            variant="outline" 
            size="icon" 
            onClick={loadData}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      <ModuleStateBoundary
        loading={loading}
        hasData={hasWhatsAppData}
        partialIssues={partialIssues}
        loadingTestId="whatsapp-loading-state"
        partialIssuesTestId="whatsapp-partial-issues"
        emptyState={(
          <EmptyStatePanel
            title={t('whatsappPage.emptyTitle')}
            description={t('whatsappPage.emptyDescription')}
            ctaLabel={t('whatsappPage.emptyCta')}
            ctaTo="/whatsapp"
            testId="whatsapp-empty-state"
          />
        )}
      >
      
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {Array.isArray(history) ? history.filter(m => m.status === 'sent' || m.status === 'delivered').length : 0}
                </p>
                <p className="text-sm text-slate-500">{t('whatsappPage.stats.sent')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {Array.isArray(history) ? history.filter(m => m.status === 'delivered' || m.status === 'read').length : 0}
                </p>
                <p className="text-sm text-slate-500">{t('whatsappPage.stats.delivered')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {Array.isArray(history) ? history.filter(m => m.status === 'failed').length : 0}
                </p>
                <p className="text-sm text-slate-500">{t('whatsappPage.stats.failed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{templates.length}</p>
                <p className="text-sm text-slate-500">{t('whatsappPage.stats.templates')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="send" className="gap-2">
            <Send className="h-4 w-4" />
            {t('whatsappPage.tabs.send')}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            {t('whatsappPage.tabs.templates')}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            {t('whatsappPage.tabs.history')}
          </TabsTrigger>
        </TabsList>
        
        {/* Send Message Tab */}
        <TabsContent value="send" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left - Template Selection */}
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  {t('whatsappPage.selectTemplateTitle')}
                </CardTitle>
                <CardDescription>
                  {t('whatsappPage.selectTemplateDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {templates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        selected={selectedTemplate === template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            
            {/* Right - Send Form */}
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-green-500" />
                  {t('whatsappPage.sendMessageTitle')}
                </CardTitle>
                <CardDescription>
                  {t('whatsappPage.sendMessageDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Phone Input */}
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('whatsappPage.phoneLabel')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone"
                      placeholder="+258 84 123 4567"
                      value={recipientPhone}
                      onChange={(e) => setRecipientPhone(e.target.value)}
                      className="pl-10"
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('whatsappPage.phoneHint')}
                  </p>
                </div>
                
                {/* Template Selected Info */}
                {selectedTemplate && (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-800">
                        {t('whatsappPage.templatePrefix')}: {templatesById[selectedTemplate]?.name}
                      </span>
                    </div>
                    <p className="text-sm text-green-700">
                      {templatesById[selectedTemplate]?.description}
                    </p>
                  </div>
                )}
                
                {/* Custom Message Option */}
                <div className="border-t pt-4">
                  <Label htmlFor="custom">{t('whatsappPage.customMessageLabel')}</Label>
                  <Textarea
                    id="custom"
                    placeholder={t('whatsappPage.customMessagePlaceholder')}
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="mt-2 min-h-[120px]"
                    data-testid="custom-message-input"
                  />
                </div>
                
                {/* Send Buttons */}
                <div className="flex gap-3 pt-4">
                  {selectedTemplate && (
                    <Button
                      onClick={handleSendTemplate}
                      disabled={sending || !recipientPhone}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      data-testid="send-template-btn"
                    >
                      {sending ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          {t('whatsappPage.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          {t('whatsappPage.sendTemplate')}
                        </>
                      )}
                    </Button>
                  )}
                  
                  {customMessage && (
                    <Button
                      onClick={handleSendCustom}
                      disabled={sending || !recipientPhone}
                      variant="outline"
                      className="flex-1"
                      data-testid="send-custom-btn"
                    >
                      {sending ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          {t('whatsappPage.sending')}
                        </>
                      ) : (
                        <>
                          <MessageCircle className="h-4 w-4 mr-2" />
                          {t('whatsappPage.sendCustom')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card className="bg-white/80 backdrop-blur border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-500" />
                {t('whatsappPage.templatesTitle')}
              </CardTitle>
              <CardDescription>
                {t('whatsappPage.templatesDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => {
                  const Icon = template.icon;
                  return (
                    <div 
                      key={template.id}
                      className={cn(
                        "p-5 rounded-xl border-2 transition-all",
                        `border-${template.color}-200 bg-gradient-to-br from-${template.color}-50/50 to-white`
                      )}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          `bg-${template.color}-100`
                        )}>
                          <Icon className={cn("h-5 w-5", `text-${template.color}-600`)} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{template.name}</p>
                          <p className="text-xs text-slate-500">ID: {template.id}</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">{template.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history">
          <Card className="bg-white/80 backdrop-blur border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5 text-blue-500" />
                    {t('whatsappPage.historyTitle')}
                  </CardTitle>
                  <CardDescription>
                    {t('whatsappPage.historyDescription', { count: history.length })}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('whatsappPage.refresh')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">{t('whatsappPage.noHistory')}</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('send')}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {t('whatsappPage.sendFirstMessage')}
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {history.map((message, idx) => (
                      <MessageItem key={message.id || idx} message={message} statusLabels={statusLabels} templatesById={templatesById} />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </ModuleStateBoundary>
      
      {/* Sandbox Dialog */}
      <Dialog open={showSandboxDialog} onOpenChange={setShowSandboxDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-green-600" />
              {t('whatsappPage.sandboxTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('whatsappPage.sandboxDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <p className="font-medium text-green-900 mb-2">{t('whatsappPage.howToRegister')}</p>
              <ol className="text-sm text-green-800 space-y-1">
                <li>{t('whatsappPage.sandboxStep1')}</li>
                <li>{t('whatsappPage.sandboxStep2')} <code className="bg-green-200 px-1 rounded">join giving-satisfied</code></li>
                <li>{t('whatsappPage.sandboxStep3')} <code className="bg-green-200 px-1 rounded">+1 415 523 8886</code></li>
                <li>{t('whatsappPage.sandboxStep4')}</li>
              </ol>
            </div>
            
            {/* QR Code placeholder */}
            <div className="flex justify-center p-6 border-2 border-dashed border-slate-200 rounded-lg">
              <div className="text-center">
                <QrCode className="h-24 w-24 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  {t('whatsappPage.qrHint')}
                </p>
              </div>
            </div>
            
            {/* Test Message */}
            <div className="space-y-2">
              <Label>{t('whatsappPage.testSend')}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="+258 84 123 4567"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                />
                <Button 
                  onClick={handleSendTest}
                  disabled={sendingTest}
                >
                  {sendingTest ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSandboxDialog(false)}>
              {t('actions.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
